import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {createUUId, deepCopy, exportCSVFile} from "../../../../../core/services/utils/utils";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {readString} from "react-papaparse";
import DropZone from "../../../base/dropzone";
import {ReactComponent as ImagePlaceholder} from "../../../../../assets/images/dropzone/upload-placeholder.svg";
import {InputFileAcceptTypes} from "../../../../../core/constants/enums";
import {SvgIcon} from "@mui/material";
import Button from "../../../base/button/button";
import {ReactComponent as Excel} from "../../../../../assets/images/excel.svg";


/**
 * A dropzone that accepts csv files and parses them.
 * @param accept
 * @param onParsed
 * @param notes
 * @param sampleFileTitle
 * @param sampleFileRowCount
 * @param {Array<ImportCsvFileRequiredField>} fields
 * @return {JSX.Element}
 */
const ImportCsvFileDropzone = ({
                                   accept = [InputFileAcceptTypes.csv],
                                   onParsed,
                                   fields,
                                   notes,
                                   sampleFileTitle,
                                   sampleFileRowCount = 10,
                               }) => {
    const requiredFieldsTitlesList = useMemo(() => fields.filter(e => !e.optional).map(e => e.title), [fields]);
    const idRef = useRef(createUUId(true));
    const [parseError, setParseError] = useState(null);
    const [parsing, setParsing] = useState(false);
    const isMounted = useIsMounted();

    /**
     * With each change in the parseError state:
     * - if set, removes the error after 2 second timeout.
     */
    useEffect(() => {
        if (!parseError)
            return;
        const timer = setTimeout(() => {
            setParseError(null);
        }, 5000)
        return () => clearTimeout(timer);
    }, [parseError])

    /**
     * Validates the parses the given csv file.
     *
     * * the file must have at least one row of entries
     * * the file's columns must at least contain the entries in the [requiredFields] list.
     * @type {(function(string): Promise<Record<string, string>[] | void>)}
     */
    const validateAndParseFile = useCallback(async (file) => {
        try {
            const csvEntries = readString(file, {header: false});
            let columns = Array.from((csvEntries?.data ? csvEntries?.data[0] : []) ?? [])
            let rows = deepCopy(csvEntries?.data?.slice(1) ?? []);
            // this is for the last line of excels, and CSVs without a value.
            rows = rows?.filter(e => e?.map(e => e?.toString() ?? "")?.filter(e => !!e)?.length);
            columns = columns?.filter((e, i) => {
                const retain = !!e;
                if (!retain) {
                    rows.forEach(row => {
                        row.splice(i, 1)
                    });
                }
                return retain;
            });

            // validate
            if (rows?.length < 1)
                return setParseError('The chosen CSV file does not contain any values.');
            if (columns.length < requiredFieldsTitlesList.length) {
                const requiredHeadersString = requiredFieldsTitlesList?.reduce((p, c, i, a) => p.concat(c + (i + 1 === a.length ? '' : ', ')), '');
                return setParseError(`The provided CSV file must contain at least ${requiredFieldsTitlesList.length} columns, optionally With the following header titles:\n ${requiredHeadersString}`)
            }

            return {
                rows,
                columns,
                csvEntries: csvEntries.data,
            };
        } catch (e) {
            setParseError('Error while parsing the provided CSV file.');
        }
    }, [requiredFieldsTitlesList])


    /**
     * Validates and parses the provided csv file.
     *
     * * if the file is parsed properly, calls the onParsed callback with the parsed file as its arg.
     * @type {(function(File[] | null): Promise<void>)}
     */
    const onFileSelected = useCallback(async (files) => {
        setParseError(null);
        const file = files[0];
        setParsing(true);
        const parsed = await validateAndParseFile(file);
        if (!isMounted())
            return;
        setParsing(false);
        if (parsed) {
            onParsed(parsed);
        }
    }, [validateAndParseFile, isMounted, onParsed])

    /**
     * Creates and exports a sample csv formatted file that has the correct format for this dropzone.
     * @type {function(): void}
     */
    const downloadSampleFile = useCallback(() => {
        const entries = Array.from({length: sampleFileRowCount ?? 10}).map((_, e) => (
            Object.fromEntries(fields.map((field) => [field.title?.concat(field.optional ? " (Optional)" : "") ?? "", field.generator?.(e) ?? ""]))
        ))
        exportCSVFile(entries, sampleFileTitle);
    }, [sampleFileTitle, fields, sampleFileRowCount])


    return (
        <>
            <DropZone
                disabled={parsing}
                identifier={'import-csv-file'}
                accept={accept}
                id={idRef.current}
                setLoading={() => (void 0)}
                onSuccess={onFileSelected}
                error={!!parseError}
                multiple={false}
                openOnClick
            >
                {() =>
                    <div className={'import-csv-file-content'}>
                        <p className={'placeholder-text'}>
                            Upload your csv file here by clicking or dragging and dropping.
                        </p>
                        <ImagePlaceholder className={'placeholder-image'}/>
                        {
                            notes
                        }
                        <Button
                            className={'download-button'}
                            variant={'text'}
                            color={'primary'}
                            onClick={(e) => e.stopPropagation() || e.preventDefault() || downloadSampleFile()}
                            disabled={parsing}
                        >
                            <SvgIcon className={'icon small'} component={Excel}/>
                            <p className='ml-2'>
                                {
                                    parsing
                                        ? 'parsing provided file'
                                        : 'Download the sample file'
                                }
                            </p>
                        </Button>
                        {
                            parseError &&
                            <p className={'placeholder-text'}>
                                {parseError}
                            </p>
                        }
                    </div>
                }
            </DropZone>
        </>
    )
}

export default ImportCsvFileDropzone;
